import { makeStyles, Box } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";

import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import { getFormatCost, getFormatStartMonth, getLatestMonthlyCashflowResult } from "@/utilities/propertyEdit";
import { PropertyHolder } from "@/view_models/atoms";

import { LatestHistoryContent } from "../../EditPropertyHistory";

import { EditPropertySectionLoanInfoHistory } from "./History";
import { LoanType } from "./types";

const useStyles = makeStyles(() => ({
  content: {
    maxWidth: 640,
    margin: "0 auto",
  },
}));

interface EditPropertySectionLoanInfoProps {
  propertyHolder: PropertyHolder;
  onClickPageBack: () => void;
}

export const EditPropertySectionLoanInfo: React.FC<EditPropertySectionLoanInfoProps> = ({
  propertyHolder,
  onClickPageBack,
}) => {
  const classes = useStyles();
  // 過去履歴の表示・非表示
  const [isShowHistory, setIsShowHistory] = React.useState(false);
  // 過去履歴ページに渡す情報
  const [selectedLoanType, setSelectedLoanType] = React.useState<LoanType | null>(null);

  /*
    各種コストの取得
  */
  //  借入利率変動
  // loanPrepayment を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultLoanInterestRatePercentage = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(
      propertyHolder.user_input_monthly_cashflow_results,
      "loan_interest_rate_percentage"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);

  const loanInterestRatePercentage =
    latestMonthlyCashflowResultLoanInterestRatePercentage?.loan_interest_rate_percentage != null
      ? `${latestMonthlyCashflowResultLoanInterestRatePercentage.loan_interest_rate_percentage}%`
      : "-%";

  // 繰上返済
  // loanPrepayment を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultLoanPrepayment = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "loan_prepayment");
  }, [propertyHolder.user_input_monthly_cashflow_results]);

  const loanPrepayment = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultLoanPrepayment?.loan_prepayment),
    [latestMonthlyCashflowResultLoanPrepayment]
  );

  // 返済方式辞書
  const loanPrepaymentMethodMap = {
    ReduceLoanAmount: "返済額軽減型",
    ReduceLoanPaymentPeriod: "返済期間短縮型",
  };
  const loanPrepaymentMethod = latestMonthlyCashflowResultLoanPrepayment?.loan_prepayment_method
    ? loanPrepaymentMethodMap[latestMonthlyCashflowResultLoanPrepayment.loan_prepayment_method]
    : "-";

  // 過去の履歴
  const onClickPageBackFromHistory = () => {
    setIsShowHistory(false);
  };
  const handleClickShowMoreHistory = (loanType: LoanType) => {
    setSelectedLoanType(loanType);
    setIsShowHistory(true);
  };
  if (isShowHistory) {
    return (
      <EditPropertySectionLoanInfoHistory loanType={selectedLoanType} onClickPageBack={onClickPageBackFromHistory} />
    );
  }

  return (
    <BaseTemplate headerTitle="借入情報" onBack={onClickPageBack} isBackGroundWhite={true}>
      <Box>
        <Box className={classes.content}>
          {/* 借入条件 */}
          <LatestHistoryContent
            title="借入条件"
            contents={[
              {
                label: "ローン借入金額",
                value: propertyHolder.property.loan_amount
                  ? `${propertyHolder.property.loan_amount.toLocaleString()}円`
                  : "-円",
              },
              {
                label: "初回返済年月",
                value: dayjs(propertyHolder.property.payment_starts_at).format("YYYY/MM"),
              },
              {
                label: "返済年数",
                value: propertyHolder.property.payment_period ? `${propertyHolder.property.payment_period}年` : "-年",
              },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("借入条件")}
          />

          {/* 借入利率変動 */}
          <LatestHistoryContent
            title="借入利率変動"
            contents={[
              {
                label: "金利の適用開始年月",
                value: getFormatStartMonth(latestMonthlyCashflowResultLoanInterestRatePercentage),
              },
              { label: "借入利率 (年利)", value: loanInterestRatePercentage },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("借入利率変動")}
          />

          {/* 繰上返済 */}
          <LatestHistoryContent
            title="繰上返済"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultLoanPrepayment) },
              { label: "返済金額", value: loanPrepayment },
              { label: "返済方式", value: loanPrepaymentMethod },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("繰上返済")}
          />
        </Box>
      </Box>
    </BaseTemplate>
  );
};
